import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * Glowora add-on toggle on the product page.
 * Tap = add to cart (with _source: complementary_upsell), tap again = remove.
 * The cart drawer stays closed unless the block setting "open_drawer" is on.
 */
class GlwAddon extends HTMLElement {
  #busy = false;
  #syncTimer = null;

  connectedCallback() {
    this.row = this.querySelector('.glw-addon__row');
    this.row?.addEventListener('click', this.#onClick);
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    this.row?.removeEventListener('click', this.#onClick);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  get #root() {
    return window.Shopify?.routes?.root || '/';
  }

  get #sectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
  }

  #setState(added) {
    this.classList.toggle('is-added', added);
    if (!this.row) return;
    this.row.setAttribute('aria-pressed', String(added));
    const title = this.querySelector('.glw-addon__title')?.textContent?.trim() || '';
    const label = added ? this.dataset.labelRemove : this.dataset.labelAdd;
    this.row.setAttribute('aria-label', `${label}: ${title}`);
  }

  #onClick = async (event) => {
    event.preventDefault();
    if (this.#busy) return;
    this.#busy = true;
    this.classList.add('is-loading');

    const sectionIds = this.#sectionIds;
    const removing = Boolean(this.dataset.inCartKey);

    try {
      let data;

      if (removing) {
        const body = { id: this.dataset.inCartKey, quantity: 0 };
        if (sectionIds.length > 0) {
          body.sections = sectionIds.join(',');
          body.sections_url = window.location.pathname;
        }
        const response = await fetch(this.#root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Remove failed');
        this.dataset.inCartKey = '';
      } else {
        const formData = new FormData();
        formData.append('id', this.dataset.variantId);
        formData.append('quantity', '1');
        formData.append('properties[_source]', 'complementary_upsell');
        if (sectionIds.length > 0) {
          formData.append('sections', sectionIds.join(','));
          formData.append('sections_url', window.location.pathname);
        }
        const response = await fetch(this.#root + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Add failed');
        this.dataset.inCartKey = data.key || '';
      }

      this.#setState(Boolean(this.dataset.inCartKey));

      let itemCount = typeof data.item_count === 'number' ? data.item_count : undefined;
      if (itemCount === undefined) {
        try {
          const cart = await fetch(this.#root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
          itemCount = cart.item_count;
        } catch (error) {
          itemCount = undefined;
        }
      }

      this.dispatchEvent(
        new CartAddEvent({}, 'glw-addon', {
          source: 'glw-addon',
          itemCount,
          variantId: String(this.dataset.variantId),
          productId: String(this.dataset.productId),
          sections: data.sections || {},
          deferDrawerOpen: !(removing === false && this.dataset.openDrawer === 'true'),
        })
      );
    } catch (error) {
      console.error('Glowora add-on:', error);
    } finally {
      this.#busy = false;
      this.classList.remove('is-loading');
    }
  };

  /** Keep the toggle in sync when the item is added or removed elsewhere (e.g. in the cart drawer). */
  #onCartUpdate = (event) => {
    if (event?.detail?.data?.source === 'glw-addon') return;
    clearTimeout(this.#syncTimer);
    this.#syncTimer = setTimeout(async () => {
      try {
        const cart = await fetch(this.#root + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const line = cart.items?.find((item) => String(item.variant_id) === String(this.dataset.variantId));
        this.dataset.inCartKey = line ? line.key : '';
        this.#setState(Boolean(line));
      } catch (error) {
        /* ignore */
      }
    }, 400);
  };
}

if (!customElements.get('glw-addon')) {
  customElements.define('glw-addon', GlwAddon);
}
